/**
 * @owner @Appboy/ingestion
 */

import type { TransformationEndpoint } from "@dashboardApi/apis/appsTransformationsDatatable";

import sharedConstants from "@lib/shared_constants";

export interface Transformation {
  id: string;
  name: string;
  url?: string;
  approval_status: string;
  updated_at: string;
  code?: string;
  last_payload?: string;
  last_headers?: string;
  total_run_count: number;
  success_run_count: number;
  is_javascript?: boolean;
  activation_available: boolean;
  endpoint: TransformationEndpoint;
}

export interface GetTransformationPayload {
  transformations: Transformation[];
}

export interface TransformationPatch {
  code?: string;
  approval_status?: JsTransformationStatus;
}

export interface TransformationValidate {
  code: string;
  last_payload: string;
  last_headers: string;
}

export interface TransformationPost {
  name: string;
  endpoint: string;
  code: string;
}

export interface CreateTransformationRequest {
  transformation: TransformationPost;
}

export interface UpdateTransformationRequest {
  transformation_id: string;
  transformation: TransformationPatch;
  propagate_errors?: boolean;
}

export interface RenameTransformationRequest {
  transformation_id: string;
  transformation_name: string;
}

export interface RenameTransformationResponse {
  transformation: Transformation;
}

export interface ValidateTransformationRequest {
  transformation_id: string;
  transformation: TransformationValidate;
}

export interface ValidateTransformationResponse {
  state: TransformationValidationState;
  output: Record<string, string>[];
}

export interface TransformationPackageDetail {
  max_active_transformations_allowed: number;
  active_transformations_count: number;
  incoming_requests: number;
  incoming_requests_limit: number;
  current_tier: number;
  app_group_transformations_breakdown: AppGroupTransformationBreakdown[];
}

export interface AppGroupTransformationBreakdown {
  ag_id: string;
  ag_name: string;
  approved_transformations_count: number;
}

export type JsTransformationStatus = "APPROVED" | "NOT_SUBMITTED" | "ARCHIVED";

const VALIDATION_STATES = sharedConstants.DATA_TRANSFORMATION.VALIDATION_STATES;
// Type is union of VALIDATION_STATES values
export type TransformationValidationState = (typeof VALIDATION_STATES)[keyof typeof VALIDATION_STATES];

export const EXECUTION_STATUS = sharedConstants.DATA_TRANSFORMATION.EXECUTION_STATUSES;

export interface GenerateTransformationRequest {
  transformation_id: string;
}

export interface GenerateTransformationResponse {
  id: string;
  code: string;
}
